import styled from "styled-components";
import { ScreenPlatform } from "@store/ducks/Screen/screen.type";

interface StylesheetProps {
  platform: ScreenPlatform;
}

export const Container = styled.div<StylesheetProps>`
  padding: ${(props) => (props.platform === ScreenPlatform.MOBILE ? "0" : "90px")} 15px 24px;
  margin-top: ${(props) => (props.platform === ScreenPlatform.MOBILE ? "5rem" : "0")};
  height: 100%;
  overflow-y: auto;
`;

export const PageTitleSection = styled.div`
  margin-bottom: 16px;

  .page-subtitle {
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 1.5px;
    color: ${(props) => props.theme.colors.primary};
    text-transform: uppercase;
    margin-bottom: 4px;
  }

  .page-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #333;
  }
`;

export const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

export const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

export const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${(props) => props.theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.85;
  }

  svg {
    font-size: 18px;
  }
`;

export const HelpButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  color: ${(props) => props.theme.colors.primary};
  border: 1px solid ${(props) => props.theme.colors.primary};
  border-radius: 4px;
  padding: 10px 16px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }

  svg {
    font-size: 18px;
  }
`;

export const GlossaryContainer = styled.div`
  width: 440px;
  max-width: 100vw;
  padding-bottom: 32px;

  @media (max-width: 600px) {
    width: 100vw;
  }

  .MuiAccordionSummary-content {
    align-items: center;
  }

  .MuiAccordionSummary-content .MuiTypography-root {
    font-size: 0.88rem;
    font-weight: 600;
    color: #333;
  }
`;

export const GlossaryHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: ${(props) => props.theme.colors.primary};
  color: #fff;

  .glossary-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1.05rem;
    font-weight: 700;
  }

  .glossary-title svg {
    font-size: 22px;
  }

  button {
    color: #fff;
  }
`;

export const GlossaryIntro = styled.div`
  padding: 16px 20px;
  background: #f5f7fa;
  border-bottom: 1px solid #e0e0e0;

  h3 {
    margin: 0 0 8px;
    font-size: 0.95rem;
    font-weight: 700;
    color: #333;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.5;
    color: #555;
  }
`;

export const GlossaryEntryBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;

  p {
    margin: 0;
    font-size: 0.85rem;
    line-height: 1.5;
    color: #444;
  }

  .entry-field {
    font-size: 0.82rem;
    color: #444;
    line-height: 1.45;
  }

  .entry-field strong {
    color: #333;
  }

  .meta-hint {
    display: block;
    margin-top: 6px;
    font-size: 0.78rem;
    font-style: italic;
    color: #777;
  }
`;

export const MetadataChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;

  .MuiChip-root {
    background: #eef2f7;
    color: #37474f;
    font-size: 0.72rem;
    height: 24px;
  }
`;

export const KpiGrid = styled.div<StylesheetProps>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.platform === ScreenPlatform.MOBILE ? "1fr" : "repeat(4, 1fr)"};
  gap: 16px;
  margin-bottom: 24px;
`;

interface KpiCardStyledProps {
  accentColor: string;
  clickable?: boolean;
}

export const KpiCard = styled.div<KpiCardStyledProps>`
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;
  border-left: 4px solid ${(props) => props.accentColor};
  overflow: hidden;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  transition: transform 0.2s, box-shadow 0.2s;

  ${(props) => props.clickable && `
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  `}

  .kpi-bg-icon {
    position: absolute;
    right: -4px;
    bottom: -8px;
    opacity: 0.06;
    font-size: 5.5rem;
    color: ${(props) => props.accentColor};
    pointer-events: none;
    line-height: 1;
  }

  .kpi-content {
    display: flex;
    flex-direction: column;
    z-index: 1;
    width: 100%;
  }

  .kpi-label {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: ${(props) => props.accentColor};
    text-transform: uppercase;
    margin-bottom: 4px;
  }

  .kpi-value-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 2px;
  }

  .kpi-value {
    font-size: 2rem;
    font-weight: 700;
    color: #333;
    line-height: 1;
  }

  .kpi-secondary {
    font-size: 0.75rem;
    color: #999;
  }

  .kpi-meta-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: ${(props) => props.accentColor};
    color: #fff;
    font-size: 0.65rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 3px;
    letter-spacing: 0.5px;
    white-space: nowrap;
  }

  .kpi-progress-track {
    width: 100%;
    height: 6px;
    background: #e0e0e0;
    border-radius: 3px;
    margin-top: 8px;
    overflow: hidden;
  }

  .kpi-progress-fill {
    height: 100%;
    border-radius: 3px;
    background: ${(props) => props.accentColor};
    transition: width 0.6s ease;
  }
`;

export const ChartsRow = styled.div<StylesheetProps>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.platform === ScreenPlatform.MOBILE ? "1fr" : "1fr 1fr"};
  gap: 16px;
  margin-bottom: 24px;
`;

export const GroupedChartsRow = styled.div<StylesheetProps>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 24px;
`;

export const GroupedChartsPairRow = styled.div<StylesheetProps>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.platform === ScreenPlatform.MOBILE ? "1fr" : "1fr 1fr"};
  gap: 16px;
  margin-bottom: 24px;
`;

export const ChartContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;

  .chart-title {
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 16px;
  }

  .chart-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    color: #999;
    font-size: 0.875rem;
  }
`;

export const MapSection = styled.div`
  margin-bottom: 24px;
`;
